import type { MeDto, MyFleetDto, Scope } from '@bqcrm/shared';
import { ActionIcon, Alert, AppShell, Avatar, Badge, Box, Burger, Button, Divider, Group, Indicator, Menu, NavLink, ScrollArea, SegmentedControl, Stack, Text, TextInput, ThemeIcon, Tooltip, UnstyledButton, useMantineColorScheme } from '@mantine/core';
import { useDisclosure, useHotkeys, useMediaQuery } from '@mantine/hooks';
import {
  IconBriefcase,
  IconBuilding,
  IconBuildingWarehouse,
  IconCar,
  IconSteeringWheel,
  IconCalendar,
  IconCalendarStats,
  IconCash,
  IconChartBar,
  IconCalendarUser,
  IconChecklist,
  IconClipboardList,
  IconFileInvoice,
  IconFileText,
  IconHash,
  IconPuzzle,
  IconHistory,
  IconHome,
  IconKeyboard,
  IconSparkles,
  IconLeaf,
  IconListCheck,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconLayoutDashboard,
  IconPackage,
  IconTruckDelivery,
  IconPlus,
  IconSearch,
  IconSelector,
  IconShieldLock,
  IconTool,
  IconUsers,
  IconUsersGroup,
  IconAddressBook, IconFolderOpen, IconRoute, IconBellCog, IconReceipt } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { QUICK_CASE_EVENT } from '../quickCase';
import { NavLink as RouterLink, Outlet, useLocation, useNavigate } from 'react-router';
import { AppFooter } from './AppFooter';
import { ErrorBoundary } from './ErrorBoundary';
import { api } from '../api';
import { can, canModule, canQuickCase } from '../auth';
import { QuickCaseModal } from './QuickCaseModal';
import { MfaSettings } from './MfaSettings';
import { NotificationSettingsModal } from './NotificationSettings';
import { NotificationBell } from './NotificationBell';
import { ShortcutsModal } from './ShortcutsModal';
import { NewVersionNotice, WhatsNew } from './WhatsNew';

interface Item {
  label: string;
  icon: ReactNode;
  to: string;
  /** moduł (uprawnienie modul.<kod>, docs/06 §5) — bez niego pozycja jest ukryta; Pulpit — dla każdego */
  module?: string;
  /** dodatkowe uprawnienie szczegółowe (pozycje modułu magazynu: np. inwentaryzacja tylko z korektami) */
  permission?: string;
  /** moduł systemu (Ustawienia → Moduły dodatkowe) — wyłączony: pozycja ukryta */
  feature?: keyof MeDto['features'];
  /** licznik spraw do zrobienia (np. zgłoszenia do umówienia) */
  count?: 'service' | 'flow';
  /** „Mój samochód” — tylko przy włączonej Flocie i przypisanym aucie (GET /fleet/my) */
  myCar?: boolean;
}

/** Menu pogrupowane wg rodzaju pracy (docs/12 „Menu”): moja praca, sprzedaż, realizacja, serwis, finanse, ustawienia. */
const GROUPS: { label: string | null; items: Item[] }[] = [
  {
    label: null,
    items: [
      { label: 'Pulpit', icon: <IconHome size={18} />, to: '/' },
      { label: 'Moje zadania', icon: <IconChecklist size={18} />, to: '/zadania', module: 'moje_zadania', count: 'flow' },
      // docs/22: miesiąc jednej osoby — zadania, nieobecności, wydarzenia i własne wpisy
      { label: 'Mój kalendarz', icon: <IconCalendarUser size={18} />, to: '/moj-kalendarz', module: 'moj_kalendarz' },
      { label: 'Moje sprawy', icon: <IconFolderOpen size={18} />, to: '/moje-sprawy', module: 'moje_sprawy' },
      // użytkownik pojazdu (docs/19 §7) — bez modułu; widoczne tylko z przypisanym autem
      { label: 'Mój samochód', icon: <IconSteeringWheel size={18} />, to: '/moj-samochod', myCar: true },
    ],
  },
  {
    label: 'Sprzedaż',
    items: [
      { label: 'Sprawy', icon: <IconBriefcase size={18} />, to: '/sprawy', module: 'sprawy' },
      { label: 'Klienci', icon: <IconAddressBook size={18} />, to: '/klienci', module: 'klienci' },
      { label: 'Katalog', icon: <IconPackage size={18} />, to: '/katalog', module: 'katalog' },
    ],
  },
  {
    label: 'Realizacja',
    items: [
      { label: 'Planowanie', icon: <IconCalendarStats size={18} />, to: '/planowanie', module: 'planowanie' },
      { label: 'Kalendarz', icon: <IconCalendar size={18} />, to: '/kalendarz', module: 'kalendarz' },
      { label: 'Ekipy', icon: <IconUsersGroup size={18} />, to: '/ekipy', module: 'ekipy' },
      { label: 'Pracownicy', icon: <IconUsers size={18} />, to: '/pracownicy', module: 'pracownicy' },
      { label: 'Flota', icon: <IconCar size={18} />, to: '/flota', module: 'flota' },
    ],
  },
  {
    label: 'Magazyn',
    items: [
      { label: 'Pulpit', icon: <IconLayoutDashboard size={18} />, to: '/magazyn/pulpit', module: 'magazyn', permission: 'magazyn.podglad' },
      { label: 'Stany', icon: <IconBuildingWarehouse size={18} />, to: '/magazyn/stany', module: 'magazyn', permission: 'magazyn.podglad' },
      { label: 'Zapotrzebowania', icon: <IconListCheck size={18} />, to: '/magazyn/zapotrzebowania', module: 'magazyn', permission: 'zapotrzebowania.podglad' },
      { label: 'Pakiety', icon: <IconPackage size={18} />, to: '/magazyn/pakiety', module: 'magazyn', permission: 'magazyn.podglad' },
      { label: 'Zamówienia', icon: <IconTruckDelivery size={18} />, to: '/magazyn/zamowienia', module: 'magazyn', permission: 'magazyn.podglad' },
      { label: 'Dokumenty', icon: <IconFileInvoice size={18} />, to: '/magazyn/dokumenty', module: 'magazyn', permission: 'magazyn.podglad' },
      { label: 'Faktury zakupu', icon: <IconReceipt size={18} />, to: '/finanse?widok=zakupy', module: 'finanse', permission: 'zakupy.podglad', feature: 'magazyn' },
      { label: 'Inwentaryzacja', icon: <IconClipboardList size={18} />, to: '/magazyn/inwentaryzacja', module: 'magazyn', permission: 'magazyn.korekty' },
    ],
  },
  {
    label: 'Serwis',
    items: [
      { label: 'Zgłoszenia i przeglądy', icon: <IconTool size={18} />, to: '/serwis', module: 'serwis', count: 'service' },
      { label: 'Instalacje', icon: <IconBuildingWarehouse size={18} />, to: '/instalacje', module: 'instalacje' },
    ],
  },
  {
    label: 'Finanse i raporty',
    items: [
      { label: 'Finanse', icon: <IconCash size={18} />, to: '/finanse', module: 'finanse' },
      { label: 'Zestawienia', icon: <IconChartBar size={18} />, to: '/zestawienia', module: 'zestawienia' },
    ],
  },
  {
    label: 'Ustawienia',
    items: [
      { label: 'Firmy', icon: <IconBuilding size={18} />, to: '/ustawienia/firmy', module: 'firmy' },
      { label: 'Numeracja', icon: <IconHash size={18} />, to: '/ustawienia/numeracja', module: 'numeracja' },
      { label: 'Moduły dodatkowe', icon: <IconPuzzle size={18} />, to: '/ustawienia/moduly', module: 'firmy', permission: 'moduly.konfiguracja:W' },
      { label: 'Magazyny i grupy', icon: <IconBuildingWarehouse size={18} />, to: '/ustawienia/magazyny', module: 'magazyn', permission: 'magazyn.konfiguracja:W' },
      { label: 'Branże i procesy', icon: <IconListCheck size={18} />, to: '/ustawienia/procesy', module: 'procesy' },
      { label: 'Zadania w sprawach', icon: <IconRoute size={18} />, to: '/ustawienia/zadania', module: 'obieg' },
      { label: 'Szablony dokumentów', icon: <IconFileText size={18} />, to: '/dokumenty/szablony', module: 'szablony' },
      { label: 'Role i uprawnienia', icon: <IconShieldLock size={18} />, to: '/ustawienia/role', module: 'role' },
      { label: 'Historia operacji', icon: <IconHistory size={18} />, to: '/historia', module: 'historia' },
    ],
  },
];

const DEMO_TEXT = 'Środowisko demonstracyjne — dane fikcyjne';

const allowed = (me: MeDto, i: Item) => {
  if (i.feature && !me.features[i.feature]) return false;
  if (i.module && !canModule(me, i.module)) return false;
  if (!i.permission) return true;
  // „kod” albo „kod:W” (wymagany zakres)
  const [code, scope] = i.permission.split(':') as [string, Scope | undefined];
  return can(me, code, scope);
};

/** Zwinięty pasek boczny (same ikony) — zapamiętany per użytkownik w tej przeglądarce. */
const collapsedKey = (userId: string) => `bqcrm.menu-zwiniete.${userId}`;
function readCollapsed(userId: string): boolean {
  try {
    return localStorage.getItem(collapsedKey(userId)) === '1';
  } catch {
    return false;
  }
}
function writeCollapsed(userId: string, v: boolean) {
  try {
    if (v) localStorage.setItem(collapsedKey(userId), '1');
    else localStorage.removeItem(collapsedKey(userId));
  } catch {
    // brak dostępu do pamięci przeglądarki — stan tylko do odświeżenia strony
  }
}

const NAV_WIDTH = 256;
const NAV_MINI_WIDTH = 68;

export function AppLayout({ me }: { me: MeDto }) {
  const [opened, { toggle, close }] = useDisclosure();
  // zwijanie paska tylko od szerokości „sm” — na telefonie menu wysuwane jak dotąd
  const wide = useMediaQuery('(min-width: 48em)', true);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(me.id));
  const mini = collapsed && wide;
  const toggleCollapsed = () => {
    writeCollapsed(me.id, !collapsed);
    setCollapsed(!collapsed);
  };
  const [quickOpen, quick] = useDisclosure();
  useEffect(() => {
    const open = () => quick.open();
    window.addEventListener(QUICK_CASE_EVENT, open);
    return () => window.removeEventListener(QUICK_CASE_EVENT, open);
  }, [quick]);
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const canSearch = can(me, 'klienci.podglad', 'W') && can(me, 'sprawy.podglad', 'W');
  const counters = useQuery({
    queryKey: ['service-counters'],
    queryFn: () => api<{ ticketsToArrange: number; overduePlans: number; protocolErrors: number } | null>('/service/counters'),
    enabled: canModule(me, 'serwis'),
    refetchInterval: 120_000,
  });
  const [shortcuts, setShortcuts] = useState(false);
  const [whatsNew, setWhatsNew] = useState(false);
  const [security, setSecurity] = useState(false);
  const [notifSettings, setNotifSettings] = useState(false);
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  useHotkeys([
    ['/', () => searchRef.current?.focus()],
    ['N', () => canQuickCase(me) && quick.open()],
    ['shift+?', () => setShortcuts(true)],
  ]);
  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    if (q.trim().length >= 2) navigate(`/szukaj?q=${encodeURIComponent(q.trim())}`);
  };
  const logout = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    qc.setQueryData(['me'], null);
    qc.clear();
  };
  const initials = me.fullName
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('');
  // zadania w sprawach: moje i do przejęcia z kolejki (odświeżane z dzwonkiem i co minutę)
  const flowCount = useQuery({ queryKey: ['process-inbox-count'], queryFn: () => api<{ mine: number; pool: number; overdue: number }>('/process/inbox/count'), refetchInterval: 60_000, enabled: can(me, 'sprawy.podglad') });
  const myFleet = useQuery({ queryKey: ['fleet', 'my'], queryFn: () => api<MyFleetDto>('/fleet/my'), enabled: me.features.flota, staleTime: 60_000, retry: false });
  const hasCar = me.features.flota && (myFleet.data?.vehicles.length ?? 0) > 0;
  const count = (i: Item) => (i.count === 'service' ? (counters.data?.ticketsToArrange ?? 0) + (counters.data?.overduePlans ?? 0) : i.count === 'flow' ? (flowCount.data?.mine ?? 0) + (flowCount.data?.pool ?? 0) : 0);

  return (
    <AppShell layout="alt" header={{ height: 60 }} navbar={{ width: mini ? NAV_MINI_WIDTH : NAV_WIDTH, breakpoint: 'sm', collapsed: { mobile: !opened } }} padding={0}>
      <AppShell.Header withBorder style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
        <Group h="100%" px="lg" justify="space-between" wrap="nowrap" gap="md">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menu" />
            {canSearch && (
              <form onSubmit={submitSearch} style={{ flex: '1 1 auto', maxWidth: 520 }}>
                <TextInput
                  ref={searchRef}
                  placeholder="Szukaj: telefon, nazwisko, adres, sprawa, praca…"
                  leftSection={<IconSearch size={16} />}
                  value={q}
                  onChange={(e) => setQ(e.currentTarget.value)}
                  variant="filled"
                  aria-label="Szukaj"
                />
              </form>
            )}
          </Group>
          <Group gap="sm" wrap="nowrap">
            {me.environment.demo && (
              <Tooltip label="Dane są fikcyjne; e-maile trafiają do testowej skrzynki" withinPortal>
                <Badge color="orange" variant="light" size="lg" visibleFrom="md">
                  {me.environment.label || DEMO_TEXT}
                </Badge>
              </Tooltip>
            )}
            <NotificationBell />
            {canQuickCase(me) && (
              <Button leftSection={<IconPlus size={16} />} onClick={quick.open}>
                <Text span inherit visibleFrom="xs">
                  Nowe zapytanie
                </Text>
              </Button>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
        <AppShell.Section>
          {mini ? (
            <Stack gap={6} align="center" py={8} mb={6}>
              <ThemeIcon size={36} radius="md" variant="gradient" gradient={{ from: 'eko.6', to: 'teal.8', deg: 135 }}>
                <IconLeaf size={20} />
              </ThemeIcon>
              <Tooltip label="Rozwiń menu" position="right" withArrow>
                <ActionIcon variant="subtle" color="gray" onClick={toggleCollapsed} aria-label="Rozwiń menu">
                  <IconLayoutSidebarLeftExpand size={18} />
                </ActionIcon>
              </Tooltip>
            </Stack>
          ) : (
            <Group gap="sm" px={6} py={8} mb={6} wrap="nowrap">
              <ThemeIcon size={36} radius="md" variant="gradient" gradient={{ from: 'eko.6', to: 'teal.8', deg: 135 }}>
                <IconLeaf size={20} />
              </ThemeIcon>
              <div style={{ lineHeight: 1.15 }}>
                <Text fw={800} size="md" style={{ letterSpacing: '-0.02em' }}>
                  Eko CRM
                </Text>
                <Text size="xs" c="dimmed">
                  instalacje i serwis
                </Text>
              </div>
              <Burger opened={opened} onClick={close} hiddenFrom="sm" size="sm" ml="auto" aria-label="Zamknij menu" />
              <Tooltip label="Zwiń menu do ikon" withArrow>
                <ActionIcon variant="subtle" color="gray" onClick={toggleCollapsed} visibleFrom="sm" ml="auto" aria-label="Zwiń menu">
                  <IconLayoutSidebarLeftCollapse size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </AppShell.Section>
        <AppShell.Section grow component={ScrollArea} mx={-4} px={4}>
          <Stack gap={2}>
            {GROUPS.map((g) => {
              const items = g.items.filter((i) => allowed(me, i) && (!i.myCar || hasCar));
              if (!items.length) return null;
              return (
                <Box key={g.label ?? 'start'} mb={6}>
                  {g.label &&
                    (mini ? (
                      <Divider my={8} mx={8} color="var(--app-border)" />
                    ) : (
                      <Text size="11px" c="dimmed" fw={700} tt="uppercase" px={12} mt={10} mb={4} style={{ letterSpacing: '0.06em' }}>
                        {g.label}
                      </Text>
                    ))}
                  {items.map((i) => {
                    const n = count(i);
                    // podpozycja o dłuższym adresie (np. /magazyn i /magazyn/stany) — aktywna tylko ona
                    const active = i.to === '/' ? location.pathname === '/' : location.pathname.startsWith(i.to) && !g.items.some((o) => o.to !== i.to && o.to.startsWith(i.to) && location.pathname.startsWith(o.to));
                    if (mini) {
                      // zwinięty: sama ikona; nazwa (z licznikiem) w podpowiedzi, licznik jako liczba na ikonie
                      return (
                        <Tooltip key={i.to} label={n > 0 ? `${i.label} (${n})` : i.label} position="right" withArrow openDelay={150}>
                          <NavLink
                            className="nav-link nav-link-mini"
                            component={RouterLink}
                            to={i.to}
                            aria-label={n > 0 ? `${i.label}, do zrobienia: ${n}` : i.label}
                            label={
                              <Indicator label={n > 99 ? '99+' : n} size={16} color="orange" disabled={n <= 0} offset={2} inline>
                                {i.icon}
                              </Indicator>
                            }
                            active={active}
                          />
                        </Tooltip>
                      );
                    }
                    return (
                      <NavLink
                        key={i.to}
                        className="nav-link"
                        component={RouterLink}
                        to={i.to}
                        label={i.label}
                        leftSection={i.icon}
                        rightSection={
                          n > 0 ? (
                            <Badge size="sm" variant="filled" color="orange" circle={n < 10}>
                              {n}
                            </Badge>
                          ) : null
                        }
                        active={active}
                        onClick={close}
                      />
                    );
                  })}
                </Box>
              );
            })}
          </Stack>
        </AppShell.Section>
        <AppShell.Section pt="sm" style={{ borderTop: '1px solid var(--app-border)' }}>
          <Menu position={mini ? 'right-end' : 'top-start'} width={240} withinPortal>
            <Menu.Target>
              <UnstyledButton w="100%" p={6} style={{ borderRadius: 'var(--mantine-radius-md)' }} aria-label={mini ? `Konto: ${me.fullName}` : undefined}>
                <Group gap="sm" wrap="nowrap" justify={mini ? 'center' : undefined}>
                  <Avatar size={36} radius="xl" color="eko" variant="light">
                    {initials}
                  </Avatar>
                  {!mini && (
                    <>
                      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
                        <Text size="sm" fw={600} truncate>
                          {me.fullName}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {me.roles.map((r) => r.name).join(', ')}
                        </Text>
                      </div>
                      <IconSelector size={16} color="var(--mantine-color-dimmed)" />
                    </>
                  )}
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {mini && <Menu.Label>{me.fullName}</Menu.Label>}
              <Menu.Label>{me.email}</Menu.Label>
              <Menu.Item leftSection={<IconShieldLock size={16} />} onClick={() => setSecurity(true)}>
                Hasło i bezpieczeństwo logowania
              </Menu.Item>
              <Menu.Item leftSection={<IconBellCog size={16} />} onClick={() => setNotifSettings(true)}>
                Powiadomienia
              </Menu.Item>
              <Menu.Item leftSection={<IconSparkles size={16} />} onClick={() => setWhatsNew(true)}>
                Co nowego
              </Menu.Item>
              <Menu.Item leftSection={<IconKeyboard size={16} />} onClick={() => setShortcuts(true)} rightSection={<span className="kbd-hint">?</span>}>
                Skróty klawiszowe
              </Menu.Item>
              <Menu.Label>Wygląd</Menu.Label>
              <Box px="xs" pb={6}>
                <SegmentedControl
                  size="xs"
                  fullWidth
                  value={colorScheme}
                  onChange={(v) => setColorScheme(v as 'light' | 'dark' | 'auto')}
                  data={[
                    { value: 'light', label: 'Jasny' },
                    { value: 'dark', label: 'Ciemny' },
                    { value: 'auto', label: 'Systemowy' },
                  ]}
                />
              </Box>
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={16} />} onClick={logout}>
                Wyloguj
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main style={{ background: 'var(--app-bg)' }}>
        <Box px={{ base: 'md', md: 'xl' }} py="lg" maw={1680} mx="auto">
          {me.environment.demo && (
            <Alert color="orange" variant="light" mb="md" hiddenFrom="md" p="xs">
              {me.environment.label || DEMO_TEXT}
            </Alert>
          )}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
          <AppFooter />
        </Box>
        {canQuickCase(me) && <QuickCaseModal opened={quickOpen} onClose={quick.close} me={me} />}
        <ShortcutsModal opened={shortcuts} onClose={() => setShortcuts(false)} />
        <WhatsNew me={me} history={whatsNew} onHistoryClose={() => setWhatsNew(false)} />
        <NewVersionNotice />
        <MfaSettings opened={security} onClose={() => setSecurity(false)} />
        <NotificationSettingsModal opened={notifSettings} onClose={() => setNotifSettings(false)} />
      </AppShell.Main>
    </AppShell>
  );
}
